import React, {useCallback, useEffect, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import styled from 'styled-components';
import {darken} from 'polished';
import fetch from 'cross-fetch';

// Automatically initializes the iframe-resizer contentWindow handler
import 'iframe-resizer';

import Fieldset, {GridFieldset} from 'components/Fieldset';
import Label, {GridLabel} from 'components/Label';
import GridField from 'components/GridField';
import Textbox from 'components/Textbox';
import PrefixedTextbox from 'components/PrefixedTextbox';
import Select from 'components/Select';
import ItemList from 'components/ItemList';
import StepForm from 'components/StepForm';
import Textarea from 'components/Textarea';
import ColorPickerInput from 'components/ColorPickerInput';

let RichTextarea;

const Preview = styled.svg`
  border-radius: 7px;
  overflow: hidden;
`;

const Step1 = () => {
  const {register, watch} = useFormContext();
  const brandColor = watch('brandColor', '#2754AA');
  const bannerTextColor = watch('bannerTextColor', '#FFFFFF');

  const brandColorValidated = brandColor.slice(0, 1) === '#'
    && !brandColor.slice(1).match(/[^A-Fa-f0-9]/)
    && (brandColor.length === 4 || brandColor.length === 7)
      ? brandColor
      : '#2754AA';
  const bannerTextColorValidated = bannerTextColor.slice(0, 1) === '#'
    && !bannerTextColor.slice(1).match(/[^A-Fa-f0-9]/)
    && (bannerTextColor.length === 4 || bannerTextColor.length === 7)
      ? bannerTextColor
      : '#FFFFFF';

  return (
    <GridFieldset
      title="Product details"
      subtitle="Step 1 of 3"
      description={(
        <>
          Let’s start with some details about your brand and the digital download you want
          to create.
        </>
      )}
      columns="1fr 370px"
      layout={[
        'productName productName',
        'businessName businessName',
        'brandColor preview',
        'bannerTextColor preview',
      ]}
      mobileColumns="1fr"
      mobileLayout={[
        'productName',
        'businessName',
        'brandColor',
        'bannerTextColor',
        'preview',
      ]}
    >
      <GridLabel title="Product name" area="productName">
        <Textbox name="productName" placeholder="e.g., How to design well" ref={register({required: true})} />
      </GridLabel>
      <GridLabel title="Business name" area="businessName">
        <Textbox name="businessName" placeholder="e.g. Company & Co" ref={register({required: true})} />
      </GridLabel>
      <GridLabel title="Brand color" area="brandColor" overflow>
        <ColorPickerInput name="brandColor" placeholder="#000000" defaultValue="#2754AA" ref={register({required: true})} />
      </GridLabel>
      <GridLabel title="Banner text color" area="bannerTextColor" overflow>
        <ColorPickerInput name="bannerTextColor" placeholder="#FFFFFF" defaultValue="#FFFFFF" ref={register({required: true})} />
      </GridLabel>
      <GridLabel title="Preview" area="preview">
        <Preview width="100%" viewBox="0 0 370 240" fill="transparent" xmlns="http://www.w3.org/2000/svg">
          <rect width="370" height="90" fill={brandColorValidated} />
          <rect x="95" y="20" width="180" height="10" rx="2" fill={bannerTextColorValidated} />
          <rect x="145" y="40" width="80" height="5" rx="2" fill={bannerTextColorValidated} fillOpacity="0.7"/>
          <rect x="158" y="60" width="10" height="10" rx="5" fill={bannerTextColorValidated} fillOpacity="0.7"/>
          <rect x="180" y="60" width="10" height="10" rx="5" fill={bannerTextColorValidated} fillOpacity="0.7"/>
          <rect x="202" y="60" width="10" height="10" rx="5" fill={bannerTextColorValidated} fillOpacity="0.7"/>
          <rect width="370" height="150" transform="translate(0 90)" fill="#FFFFFF" />
          <rect x="45" y="120" width="270" height="5" rx="2" fill={darken(0.7, bannerTextColorValidated)} />
          <rect x="45" y="135" width="280" height="5" rx="2" fill={darken(0.7, bannerTextColorValidated)} />
          <rect x="45" y="150" width="260" height="5" rx="2" fill={darken(0.7, bannerTextColorValidated)} />
          <circle cx="50" cy="175" r="5" fill={brandColorValidated} fillOpacity="0.1"/>
          <rect x="60" y="172.5" width="140" height="5" rx="2" fill={darken(0.7, bannerTextColorValidated)} />
          <circle cx="50" cy="190" r="5" fill={brandColorValidated} fillOpacity="0.1"/>
          <rect x="60" y="187.5" width="140" height="5" rx="2" fill={darken(0.7, bannerTextColorValidated)} />
          <circle cx="50" cy="205" r="5" fill={brandColorValidated} fillOpacity="0.1"/>
          <rect x="60" y="202.5" width="140" height="5" rx="2" fill={darken(0.7, bannerTextColorValidated)} />
          <rect x="0.5" y="0.5" width="369" height="239" rx="7.5" stroke="#1A1715" strokeOpacity="0.15"/>
        </Preview>
      </GridLabel>
    </GridFieldset>
  );
};

Step1.fields = [
  'productName',
  'businessName',
  'brandColor',
  'bannerTextColor',
];

const Step2 = () => {
  const {register, watch, setValue} = useFormContext();
  const productType = watch('productType');
  register('description');
  const updateDescription = useCallback((value) => {
    setValue('description', value);
  });

  return (
    <GridFieldset
      title="Download content"
      subtitle="Step 2 of 3"
      description={(
        <>
          Next, we’ll create the content for your download.
        </>
      )}
      columns="1fr"
      layout={[
        'productType',
        'description',
        'checklist',
      ]}
    >
      <GridLabel title="Product type" area="productType" overflow>
        <Select
          name="productType"
          options={[
            {value: 'checklist', label: 'Checklist'},
            {value: 'text-images', label: 'Text & Images'},
          ]}
        />
      </GridLabel>
      {productType === 'checklist' && (
        <>
          <GridLabel title="Description" area="description" description="This will appear at the top of the first page and tell your reader what this download will help them do.">
            <Textarea name="description" placeholder="e.g. Pretty good, not great!" rows={3} ref={register} />
          </GridLabel>
          <GridLabel title="Checklist" area="checklist">
            <ItemList name="checklist" addLabel="Add list item" maxLength={150} />
          </GridLabel>
        </>
      )}
      {productType === 'text-images' && (
        <GridLabel title="Description" area="description">
          <RichTextarea
            name="description"
            onChange={updateDescription}
            placeholder="Fill in this field with any text or images you'd like (you can link images where you want them)."
          />
        </GridLabel>
      )}
    </GridFieldset>
  );
};

Step2.fields = [
  'productType',
  'description',
  'checklist',
];

const Step3 = () => {
  const {register} = useFormContext();

  return (
    <GridFieldset
      title="About yourself"
      subtitle="Step 3 of 3"
      description={(
        <>
          Last step! Tell us a bit about yourself so we can customize your download and send
          it to you!
        </>
      )}
      columns="1fr"
      layout={[
        'links',
        'email',
      ]}
    >
      <GridLabel
        title="Links"
        description="Add social and custom links to your download to help grow your audience."
        area="links"
        optional
      >
        <GridField columns="1fr 1fr" rows="repeat(8, 1fr)">
          <PrefixedTextbox
            icon={(
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.1918 0H2.82298C1.2696 0 0 1.27436 0 2.83358V17.1514C0 18.7256 1.2696 19.985 2.82298 19.985H10.6646V11.3343H8.87223V8.3958H10.6646C10.6646 8.3958 10.6646 7.97601 10.6646 6.07196C10.6646 3.89805 11.8296 2.75862 14.3987 2.75862C14.8169 2.75862 16.7885 2.75862 16.7885 2.75862V5.77211C16.7885 5.77211 15.4443 5.77211 14.9215 5.77211C14.2643 5.77211 14.1299 6.04198 14.1299 6.73163C14.1299 7.30135 14.1299 8.3958 14.1299 8.3958H16.8035L16.5496 11.3043H14.1149V20H17.1769C18.7452 20 19.9999 18.7256 19.9999 17.1664V2.83358C20.0148 1.27436 18.7452 0 17.1918 0Z" fill="#9C8881" />
              </svg>
            )}
            prefix="https://facebook.com/"
            placeholder="username"
            name="facebook"
            ref={register}
          />
          <Textbox name="facebookLabel" placeholder="e.g. Facebook" maxLength={50} ref={register} />
          <PrefixedTextbox
            icon={(
              <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 2.30636C23.117 2.69764 22.168 2.96212 21.172 3.08089C22.1885 2.47215 22.9693 1.50826 23.3369 0.359623C22.3855 0.923323 21.3317 1.33264 20.2102 1.55319C19.312 0.597218 18.0323 -1.81138e-07 16.6161 -1.81138e-07C13.4378 -1.81138e-07 11.1015 2.96287 11.8198 6.03951C7.72753 5.83444 4.09934 3.87613 1.67085 0.900395C0.381029 3.11068 1.00183 6.00326 3.19462 7.46729C2.38759 7.44181 1.62816 7.2205 0.964427 6.85198C0.91101 9.13087 2.54575 11.2618 4.91365 11.7371C4.22068 11.9255 3.46165 11.9686 2.69016 11.8213C3.3166 13.7754 5.13513 15.1974 7.28977 15.2371C5.21965 16.8576 2.61239 17.5815 0 17.2739C2.17899 18.6695 4.7672 19.4839 7.54783 19.4839C16.6895 19.4839 21.8543 11.7705 21.543 4.85252C22.5052 4.15905 23.3399 3.29276 24 2.30636Z" fill="#9C8881" />
              </svg>
            )}
            prefix="https://twitter.com/"
            placeholder="username"
            name="twitter"
            ref={register}
          />
          <Textbox name="twitterLabel" placeholder="e.g. Twitter" maxLength={50} ref={register} />
          <PrefixedTextbox
            icon={(
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M15.445 2.04615C14.2849 1.99323 13.9371 1.98201 10.9999 1.98201L11 1.98196C8.06282 1.98196 7.71494 1.99318 6.555 2.04611C5.48252 2.09501 4.90009 2.27421 4.51248 2.42485C4.0347 2.60119 3.60252 2.88236 3.2477 3.2477C2.88233 3.60252 2.60112 4.03472 2.42476 4.51252C2.27412 4.90017 2.09497 5.48257 2.04602 6.55505C1.9931 7.71508 1.98188 8.06286 1.98188 11.0001C1.98188 13.9373 1.9931 14.2852 2.04602 15.4451C2.09492 16.5176 2.27412 17.1 2.42476 17.4876C2.6011 17.9654 2.88228 18.3976 3.24761 18.7524C3.60243 19.1177 4.03461 19.3989 4.51239 19.5752C4.90004 19.7258 5.48244 19.905 6.55492 19.954C7.71477 20.0069 8.06251 20.0181 10.9999 20.0181C13.9374 20.0181 14.2852 20.0069 15.445 19.954C16.5174 19.9051 17.0999 19.7259 17.4875 19.5752C17.9619 19.3922 18.3927 19.1119 18.7523 18.7524C19.1118 18.3928 19.3921 17.962 19.5751 17.4876C19.7257 17.1 19.9049 16.5176 19.9539 15.4451C20.0068 14.2851 20.018 13.9373 20.018 11.0001C20.018 8.06286 20.0068 7.71499 19.9539 6.55505C19.9049 5.48257 19.7257 4.90013 19.5751 4.51252C19.3988 4.03475 19.1176 3.60256 18.7523 3.24775C18.3974 2.88241 17.9653 2.60123 17.4875 2.42489C17.0998 2.27425 16.5174 2.0951 15.445 2.04615ZM6.46479 0.066195C7.63787 0.0126631 8.0126 2.48699e-07 10.9999 0C13.9874 0 14.3621 0.0126626 15.5352 0.0664133C16.706 0.11964 17.5056 0.305562 18.2053 0.577285C18.9394 0.853547 19.6043 1.28662 20.1537 1.8463C20.7133 2.39574 21.1463 3.06063 21.4225 3.7946C21.6944 4.49427 21.8804 5.29385 21.9338 6.46471C21.9873 7.63779 22 8.01256 22 10.9999C22 13.9873 21.9873 14.3619 21.9338 15.5352C21.8804 16.706 21.6944 17.5056 21.4225 18.2053C21.1399 18.936 20.7077 19.5996 20.1538 20.1536C19.5998 20.7076 18.9362 21.1397 18.2054 21.4223C17.5058 21.6943 16.7062 21.8802 15.5353 21.9336C14.3623 21.9872 13.9875 21.9998 11.0001 21.9998C8.01273 21.9998 7.63814 21.9872 6.46488 21.9336C5.29407 21.8802 4.49449 21.6943 3.79477 21.4223C3.0608 21.1461 2.39591 20.7131 1.84647 20.1536C1.28691 19.6041 0.853926 18.9392 0.577721 18.2053C0.305781 17.5056 0.119858 16.706 0.0664133 15.5352C0.0126626 14.3621 0 13.9874 0 10.9999C0 8.01252 0.0126626 7.63779 0.0664133 6.46471C0.11964 5.29385 0.305562 4.49427 0.577285 3.79455C0.853591 3.06055 1.28669 2.39566 1.84639 1.84625C2.39583 1.28669 3.06071 0.853707 3.79468 0.577503C4.49436 0.305562 5.29394 0.11964 6.46479 0.066195ZM16.8717 6.44808C17.6007 6.44808 18.1917 5.85711 18.1917 5.12811C18.1917 4.39911 17.6007 3.80814 16.8717 3.80814C16.1427 3.80814 15.5517 4.39911 15.5517 5.12811C15.5517 5.85711 16.1427 6.44808 16.8717 6.44808ZM11.0001 5.35156C9.88292 5.35156 8.79082 5.68284 7.86191 6.30352C6.933 6.9242 6.209 7.80639 5.78147 8.83854C5.35394 9.8707 5.24208 11.0064 5.46003 12.1022C5.67798 13.1979 6.21596 14.2044 7.00593 14.9944C7.79591 15.7843 8.8024 16.3223 9.89812 16.5403C10.9938 16.7582 12.1296 16.6464 13.1617 16.2188C14.1939 15.7913 15.0761 15.0673 15.6968 14.1384C16.3174 13.2095 16.6487 12.1174 16.6487 11.0002C16.6487 9.50207 16.0536 8.06532 14.9943 7.006C13.935 5.94668 12.4982 5.35156 11.0001 5.35156ZM11.0001 14.6668C10.2749 14.6668 9.56603 14.4517 8.96306 14.0488C8.3601 13.6459 7.89014 13.0733 7.61263 12.4033C7.33512 11.7333 7.26252 10.9961 7.404 10.2848C7.54548 9.57358 7.8947 8.92026 8.40748 8.40747C8.92027 7.89469 9.5736 7.54549 10.2849 7.40401C10.9961 7.26254 11.7333 7.33515 12.4033 7.61267C13.0733 7.89019 13.6459 8.36015 14.0488 8.96312C14.4517 9.56609 14.6668 10.275 14.6668 11.0002C14.6668 11.9726 14.2804 12.9052 13.5928 13.5929C12.9052 14.2805 11.9726 14.6668 11.0001 14.6668Z" fill="#9C8881" />
              </svg>
            )}
            prefix="https://instagram.com/"
            placeholder="username"
            name="instagram"
            ref={register}
          />
          <Textbox name="instagramLabel" placeholder="e.g. Instagram" maxLength={50} ref={register} />
          <PrefixedTextbox
            icon={(
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M17.0423 17.0424H14.0779V12.4009C14.0779 11.2936 14.0576 9.87 12.5365 9.87C10.9928 9.87 10.7564 11.0758 10.7564 12.3208V17.0424H7.79211V7.49795H10.6378V8.80196H10.6772C11.073 8.05169 12.0407 7.26046 13.4838 7.26046C16.4877 7.26046 17.0423 9.23723 17.0423 11.8071V17.0424ZM4.4491 6.19325C3.49828 6.19325 2.72871 5.42229 2.72871 4.47286C2.72871 3.52412 3.49828 2.75343 4.4491 2.75343C5.39743 2.75343 6.16811 3.52412 6.16811 4.47286C6.16811 5.42229 5.39743 6.19325 4.4491 6.19325ZM2.96524 17.0424H5.93172V7.49795H2.96524V17.0424ZM18.5196 0H1.47623C0.661796 0 0 0.645547 0 1.44151V18.5565C0 19.3532 0.661796 19.9997 1.47623 19.9997H18.5196C19.3354 19.9997 20 19.3532 20 18.5565V1.44151C20 0.645547 19.3354 0 18.5196 0Z" fill="#9C8881" />
              </svg>
            )}
            prefix="https://linkedin.com/"
            placeholder="username"
            name="linkedin"
            ref={register}
          />
          <Textbox name="linkedinLabel" placeholder="e.g. LinkedIn" maxLength={50} ref={register} />
          <PrefixedTextbox
            icon={(
              <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.9998 2.2432e-05C9.47036 -0.00508372 7.01647 0.86168 5.05142 2.45432C3.08637 4.04696 1.73023 6.26816 1.21143 8.74379C0.692619 11.2194 1.04284 13.7982 2.20311 16.0458C3.36338 18.2934 5.26279 20.0725 7.58143 21.0834C7.55393 20.3042 7.58143 19.3875 7.77393 18.5534L9.19476 12.5584C9.19476 12.5584 8.83726 11.8525 8.83726 10.8167C8.83726 9.18502 9.78143 7.97502 10.9639 7.97502C11.9631 7.97502 12.4489 8.71752 12.4489 9.62502C12.4489 10.6242 11.8073 12.1275 11.4773 13.5209C11.2023 14.685 12.0548 15.6384 13.2098 15.6384C15.2906 15.6384 16.6931 12.9617 16.6931 9.79002C16.6931 7.37919 15.0706 5.57336 12.1189 5.57336C11.413 5.54593 10.7088 5.66247 10.0493 5.91587C9.38983 6.16927 8.78881 6.55424 8.28285 7.04733C7.77689 7.54043 7.37658 8.13134 7.10628 8.78408C6.83598 9.43683 6.70135 10.1378 6.71059 10.8442C6.71059 11.8067 6.98559 12.4759 7.42559 12.9984C7.63643 13.2459 7.66393 13.3375 7.59059 13.6125L7.36143 14.5017C7.28809 14.7767 7.06809 14.8684 6.81143 14.7767C5.28059 14.1442 4.55643 12.4667 4.55643 10.5692C4.55643 7.45252 7.19643 3.69419 12.4214 3.69419C16.6198 3.69419 19.3881 6.73752 19.3881 10.0009C19.3881 14.3092 16.9864 17.5359 13.4481 17.5359C12.2564 17.5359 11.1473 16.8942 10.7623 16.1609L9.99226 19.1859C9.75393 20.0384 9.29559 20.8909 8.88309 21.5509C10.5241 22.0377 12.2562 22.1345 13.9412 21.8335C15.6263 21.5326 17.2177 20.8423 18.5888 19.8175C19.9599 18.7928 21.0726 17.462 21.8384 15.9311C22.6043 14.4003 23.0019 12.7117 22.9998 11C22.9998 8.08264 21.8408 5.28475 19.7779 3.22185C17.715 1.15895 14.9171 2.2432e-05 11.9998 2.2432e-05Z" fill="#9C8881" />
              </svg>
            )}
            prefix="https://pinterest.com/"
            placeholder="username"
            name="pinterest"
            ref={register}
          />
          <Textbox name="pinterestLabel" placeholder="e.g. Pinterest" maxLength={50} ref={register} />
          <PrefixedTextbox
            icon={(
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.3335 10.8335C8.69137 11.3119 9.14796 11.7078 9.67229 11.9943C10.1966 12.2807 10.7764 12.4511 11.3724 12.4937C11.9683 12.5364 12.5665 12.4504 13.1263 12.2416C13.6861 12.0328 14.1944 11.706 14.6168 11.2835L17.1168 8.78347C17.8758 7.99762 18.2958 6.94512 18.2863 5.85263C18.2768 4.76014 17.8386 3.71509 17.0661 2.94256C16.2935 2.17002 15.2485 1.73182 14.156 1.72233C13.0635 1.71283 12.011 2.13281 11.2252 2.8918L9.79183 4.3168" stroke="#9C8881" strokeWidth="2" strokeLinecap="round" />
                <path d="M11.6668 9.16702C11.309 8.68858 10.8524 8.2927 10.328 8.00623C9.80371 7.71977 9.22391 7.54942 8.62796 7.50674C8.03201 7.46406 7.43384 7.55004 6.87405 7.75887C6.31425 7.96769 5.8059 8.29446 5.3835 8.71702L2.8835 11.217C2.12451 12.0029 1.70453 13.0554 1.71402 14.1479C1.72352 15.2403 2.16172 16.2854 2.93426 17.0579C3.70679 17.8305 4.75184 18.2687 5.84433 18.2782C6.93681 18.2877 7.98932 17.8677 8.77517 17.1087L10.2002 15.6837" stroke="#9C8881" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
            prefix="https://"
            placeholder="www.website.com"
            name="website1"
            ref={register}
          />
          <Textbox name="website1Label" placeholder="e.g. My Website" maxLength={50} ref={register} />
          <PrefixedTextbox
            icon={(
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.3335 10.8335C8.69137 11.3119 9.14796 11.7078 9.67229 11.9943C10.1966 12.2807 10.7764 12.4511 11.3724 12.4937C11.9683 12.5364 12.5665 12.4504 13.1263 12.2416C13.6861 12.0328 14.1944 11.706 14.6168 11.2835L17.1168 8.78347C17.8758 7.99762 18.2958 6.94512 18.2863 5.85263C18.2768 4.76014 17.8386 3.71509 17.0661 2.94256C16.2935 2.17002 15.2485 1.73182 14.156 1.72233C13.0635 1.71283 12.011 2.13281 11.2252 2.8918L9.79183 4.3168" stroke="#9C8881" strokeWidth="2" strokeLinecap="round" />
                <path d="M11.6668 9.16702C11.309 8.68858 10.8524 8.2927 10.328 8.00623C9.80371 7.71977 9.22391 7.54942 8.62796 7.50674C8.03201 7.46406 7.43384 7.55004 6.87405 7.75887C6.31425 7.96769 5.8059 8.29446 5.3835 8.71702L2.8835 11.217C2.12451 12.0029 1.70453 13.0554 1.71402 14.1479C1.72352 15.2403 2.16172 16.2854 2.93426 17.0579C3.70679 17.8305 4.75184 18.2687 5.84433 18.2782C6.93681 18.2877 7.98932 17.8677 8.77517 17.1087L10.2002 15.6837" stroke="#9C8881" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
            prefix="https://"
            placeholder="www.website.com"
            name="website2"
            ref={register}
          />
          <Textbox name="website2Label" placeholder="e.g. My Website" maxLength={50} ref={register} />
          <PrefixedTextbox
            icon={(
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.3335 10.8335C8.69137 11.3119 9.14796 11.7078 9.67229 11.9943C10.1966 12.2807 10.7764 12.4511 11.3724 12.4937C11.9683 12.5364 12.5665 12.4504 13.1263 12.2416C13.6861 12.0328 14.1944 11.706 14.6168 11.2835L17.1168 8.78347C17.8758 7.99762 18.2958 6.94512 18.2863 5.85263C18.2768 4.76014 17.8386 3.71509 17.0661 2.94256C16.2935 2.17002 15.2485 1.73182 14.156 1.72233C13.0635 1.71283 12.011 2.13281 11.2252 2.8918L9.79183 4.3168" stroke="#9C8881" strokeWidth="2" strokeLinecap="round" />
                <path d="M11.6668 9.16702C11.309 8.68858 10.8524 8.2927 10.328 8.00623C9.80371 7.71977 9.22391 7.54942 8.62796 7.50674C8.03201 7.46406 7.43384 7.55004 6.87405 7.75887C6.31425 7.96769 5.8059 8.29446 5.3835 8.71702L2.8835 11.217C2.12451 12.0029 1.70453 13.0554 1.71402 14.1479C1.72352 15.2403 2.16172 16.2854 2.93426 17.0579C3.70679 17.8305 4.75184 18.2687 5.84433 18.2782C6.93681 18.2877 7.98932 17.8677 8.77517 17.1087L10.2002 15.6837" stroke="#9C8881" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
            prefix="https://"
            placeholder="www.website.com"
            name="website3"
            ref={register}
          />
          <Textbox name="website3Label" placeholder="e.g. My Website" maxLength={50} ref={register} />
        </GridField>
      </GridLabel>
      <GridLabel
        title="Your email"
        description="This is where we’ll send the download file."
        area="email"
      >
        <Textbox name="email" ref={register({required: true, pattern: /^[^@]+@[^.]+\..+$/})} />
      </GridLabel>
    </GridFieldset>
  );
};

const Steps = [
  Step1,
  Step2,
  Step3,
];

const DigitalDownload = () => {
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = useCallback(async (values) => {
    setSubmitting(true);

    try {
      await fetch('/api/digital-download', {
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values),
      });

      if (window.parentIFrame) {
        window.parentIFrame.sendMessage({complete: true});
      }
    } catch (err) {
      setSubmitting(false);
    }
  }, []);
  useEffect(async () => {
    RichTextarea = (await import('components/RichTextarea')).default;
  }, []);

  return (
    <StepForm
      steps={Steps}
      submitLabel="Generate download"
      loadingLabel="Generating, please wait…"
      onSubmit={onSubmit}
      submitting={submitting}
      defaultValues={{
        checklist: [{}, {}, {}],
        productType: 'checklist',
      }}
    />
  );
};

export default DigitalDownload;
